import React, { useState, useRef, useEffect, lazy, Suspense } from "react";
import "yet-another-react-lightbox/styles.css";
import "swiper/css";
import "swiper/css/pagination";
import "swiper/css/navigation";
import { motion } from "framer-motion";
import { GiSpeakerOff } from "react-icons/gi";
import { HiMiniSpeakerWave } from "react-icons/hi2";

const Lightbox = lazy(() => import("yet-another-react-lightbox"));
const facilityImages = [
  {
    src: "https://executiveinn.s3.eu-north-1.amazonaws.com/img/Dfrontview.jpg",
    alt: "Daytime front view of Executive Inn hotel",
  },
  {
    src: "https://executiveinn.s3.eu-north-1.amazonaws.com/img/downangle.jpg",
    alt: "Executive Inn building from downward angle view",
  },
  {
    src: "https://executiveinn.s3.eu-north-1.amazonaws.com/img/front.jpg",
    alt: "Executive Inn hotel front with parking area",
  },
  {
    src: "https://executiveinn.s3.eu-north-1.amazonaws.com/assets/Facilities/parking3.jpg",
    alt: "Parking area in front of Executive Inn",
  },
  {
    src: "https://executiveinn.s3.eu-north-1.amazonaws.com/img/FrontView.jpg",
    alt: "Close-up of Executive Inn front entrance",
  },
  {
    src: "https://executiveinn.s3.eu-north-1.amazonaws.com/assets/Facilities/Nlone2.jpg",
    alt: "Side exterior view of Executive Inn building",
  },
  {
    src: "https://executiveinn.s3.eu-north-1.amazonaws.com/assets/Facilities/Nlone3.jpg",
    alt: "Distant angle view of Executive Inn building exterior",
  },
  {
    src: "https://executiveinn.s3.eu-north-1.amazonaws.com/assets/Facilities/Entry.jpg",
    alt: "Executive Inn hotel entrance walkway",
  },
  {
    src: "https://executiveinn.s3.eu-north-1.amazonaws.com/assets/Facilities/Nparking1.jpg",
    alt: "Executive Inn large parking lot view 1",
  },
  {
    src: "https://executiveinn.s3.eu-north-1.amazonaws.com/assets/Facilities/Nparking2.jpg",
    alt: "Executive Inn parking space view 2",
  },
  {
    src: "https://executiveinn.s3.eu-north-1.amazonaws.com/assets/Facilities/Nparking3.jpg",
    alt: "Wide view of Executive Inn parking area",
  },
  {
    src: "https://executiveinn.s3.eu-north-1.amazonaws.com/assets/Facilities/parkingsit2.jpg",
    alt: "Outdoor seating beside Executive Inn parking",
  },
  {
    src: "https://executiveinn.s3.eu-north-1.amazonaws.com/assets/Facilities/parkingsit.jpg",
    alt: "Outdoor seating area at Executive Inn",
  },
  {
    src: "https://executiveinn.s3.eu-north-1.amazonaws.com/img/leftside.jpg",
    alt: "Nighttime view of Executive Inn pool area",
  },
  {
    src: "https://executiveinn.s3.eu-north-1.amazonaws.com/img/pool2.jpg",
    alt: "Executive Inn pool with surrounding deck",
  },
  {
    src: "https://executiveinn.s3.eu-north-1.amazonaws.com/assets/HomePage/day-poolArea.jpg",
    alt: "Executive Inn pool area during the day",
  },
  {
    src: "https://executiveinn.s3.eu-north-1.amazonaws.com/img/pool3.jpg",
    alt: "Close-up view of Executive Inn swimming pool",
  },
  {
    src: "https://executiveinn.s3.eu-north-1.amazonaws.com/assets/Booking/pool.jpg",
    alt: "Executive Inn poolside lounge chairs and water view",
  },
];

const HeroSlider = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [photoIndex, setPhotoIndex] = useState(0);
  const videoRef = useRef(null);
  const [isMuted, setIsMuted] = useState(true);

  useEffect(() => {
    const video = videoRef.current;
    if (video) {
      video.muted = isMuted;
      video.play().catch((err) => console.warn("Autoplay blocked:", err));
    }
    return () => video && video.pause();
  }, [isMuted]);

  return (
    <main className="relative pt-20 min-h-screen bg-white">
      {/* Preload Image */}
      <link
        rel="preload"
        as="image"
        href="https://executiveinn.s3.eu-north-1.amazonaws.com/assets/HomePage/front-day.jpg"
      />

      {/* Video Header */}
      <header className="relative w-full z-10">
        <div className="relative w-full overflow-visible group">
          <video
            ref={videoRef}
            src="https://executiveinn.s3.eu-north-1.amazonaws.com/HomeVideo/Vid3.mp4"
            autoPlay
            loop
            muted={isMuted}
            playsInline
            preload="metadata"
            poster="https://executiveinn.s3.eu-north-1.amazonaws.com/HomeVideo/Vid3-poster.jpg"
            className="w-full object-cover max-h-[600px] h-auto"
            width={800}
            height={600}
            aria-label="Executive Inn promotional video"
          />
          <button
            onClick={() => setIsMuted((prev) => !prev)}
            className="absolute bottom-5 left-5 bg-black bg-opacity-60 text-white p-2 rounded"
            title={isMuted ? "Unmute video" : "Mute video"}
          >
            {isMuted ? (
              <GiSpeakerOff size={24} />
            ) : (
              <HiMiniSpeakerWave size={24} />
            )}
          </button>
          <div className="absolute bottom-0 left-0 w-full h-[10px] bg-[#007f66]" />
        </div>
      </header>

      {/* Tagline */}
      <section className="py-8 px-4 md:px-10 text-center">
        <h2 className="text-3xl font-bold">Extended Stay Available</h2>
      </section>

      {/* Facility Section */}
      <section
        className="relative z-10 mx-auto w-full max-w-6xl overflow-hidden bg-[#f1f1f1] shadow-[0_0_70px_-20px_rgba(0,0,0,0.8)] flex flex-col lg:flex-row items-center px-6 sm:px-10 lg:px-16 py-10 lg:py-14 gap-8"
        aria-label="Facility images and description"
      >
        {/* Text Block */}
        <motion.article
          className="flex-1 max-w-2xl text-center lg:text-left"
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, ease: "easeOut" }}
        >
          <h3
            className="mb-3 text-4xl sm:text-5xl text-center font-extrabold uppercase tracking-wide text-green-700"
            tabIndex={-1}
          >
            Our Facility
          </h3>
          <hr className="border-t-3 border-gray-200 w-20 mt-2 mx-auto" />

          <motion.p
            className="text-center mt-6 whitespace-pre-line font-semibold leading-relaxed text-gray-900"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ delay: 0.3, duration: 0.7 }}
          >
            {`Whether you're visiting Chester or exploring the surrounding areas, Executive Inn offers a comfortable and convenient stay. Enjoy well-appointed guest rooms and thoughtful amenities such as Free Wi-Fi, Microwave, Free Parking and Pool (seasonal) and other essentials designed to enhance your visit. Located just minutes from local attractions, including the popular Italian Garden II restaurant and the Arts Council gallery, our motel puts you in the heart of it all. We look forward to making your stay in Chester both relaxing and memorable.`}
          </motion.p>
        </motion.article>

        {/* Gallery */}
        <figure className="grid flex-1 max-w-2xl gap-[2px] sm:gap-1 md:gap-2 lg:gap-3 grid-cols-2 sm:grid-cols-3 md:grid-cols-4">
          {facilityImages.map(({ src, alt }, i) => (
            <button
              key={i}
              type="button"
              onClick={() => {
                setPhotoIndex(i);
                setIsOpen(true);
              }}
              className="relative aspect-square overflow-hidden rounded focus:outline-none"
              aria-label={`Open image ${i + 1}: ${alt}`}
            >
              <img
                src={src}
                alt={alt}
                width={300}
                height={300}
                loading={i < 2 ? "eager" : "lazy"}
                decoding="async"
                className="h-full w-full object-cover transition-transform hover:scale-105"
              />
            </button>
          ))}
        </figure>
      </section>

      {/* Quote Section */}
      <section className="relative w-full h-[500px] mt-16 overflow-hidden shadow-xl">
        <img
          src="https://executiveinn.s3.eu-north-1.amazonaws.com/img/kitchen.jpg"
          alt="Hotel living room banner"
          loading="lazy"
          decoding="async"
          width={1920}
          height={500}
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute top-0 left-0 w-full h-full bg-black opacity-60" />
        <div className="absolute inset-0 flex items-center justify-center px-6">
          <blockquote className="text-white text-3xl font-medium text-center italic max-w-3xl mx-auto">
            “Comfortable Rooms, Friendly Staff, Free WiFi and HBO”
          </blockquote>
        </div>
      </section>

      {/* Lightbox */}
      {isOpen && (
        <Suspense
          fallback={
            <div className="fixed inset-0 z-[60] flex items-center justify-center bg-white bg-opacity-80">
              Loading gallery...
            </div>
          }
        >
          <div className="fixed inset-0 z-[60]" role="dialog" aria-modal="true">
            <Lightbox
              open={isOpen}
              close={() => setIsOpen(false)}
              index={photoIndex}
              slides={facilityImages.map(({ src, alt }) => ({ src, alt }))}
              on={{ view: ({ index }) => setPhotoIndex(index) }}
              styles={{
                container: {
                  backgroundColor: "rgba(255, 255, 255, 0.2)",
                  backdropFilter: "blur(4px)",
                },
              }}
            />
          </div>
        </Suspense>
      )}
    </main>
  );
};

export default HeroSlider;
